import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo, useCallback, useState} from 'react'
import type {ThreadPreview as PendingCommentPreviewType} from '../../page-data/payloads/thread-previews'
import type {Author, Comments} from '@github-ui/diff-lines/conversation-types'
import {ConversationHeader} from './ConversationHeader'
import {PreviewAuthors} from './PreviewAuthors'
import type {SafeHTMLString} from '@github-ui/safe-html'

interface PendingCommentPreviewProps {
  commentPreview: PendingCommentPreviewType
  onNavigateToDiffComment: (threadId: string) => void
  tabSize?: number
}

export const PendingCommentPreview = memo(function PendingCommentPreview({
  commentPreview,
  onNavigateToDiffComment,
  tabSize,
}: PendingCommentPreviewProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const commentId = commentPreview.commentId
  const navigateToComment = useCallback(() => {
    window.location.hash = `#r${commentId}`
    onNavigateToDiffComment(commentId)
  }, [commentId, onNavigateToDiffComment])
  const commentAuthors = extractCommentAuthors(commentId, commentPreview.commentsData ?? {comments: []})
  const previousCommentCount = commentPreview.threadPreviewComments.length
  const previousCommentText = `${previousCommentCount} previous ${previousCommentCount === 1 ? 'comment' : 'comments'}`

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ConversationHeader
        isCollapsed={isCollapsed}
        isOutdated={commentPreview.isOutdated || false}
        isResolved={commentPreview.isResolved || false}
        line={commentPreview.line}
        path={commentPreview.path}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(collapsed => !collapsed)}
        threadId={commentPreview.threadId}
        firstCommentId={parseInt(commentPreview.commentId)}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={commentPreview.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          {previousCommentCount > 0 && (
            <div className="my-2 px-2">
              <Button
                aria-label="View comment in diff"
                size="small"
                trailingVisual={ChevronRightIcon}
                variant="invisible"
                onClick={navigateToComment}
              >
                <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                  <span>{previousCommentText}</span>
                  <PreviewAuthors commentAuthors={commentAuthors} />
                </div>
              </Button>
            </div>
          )}
          <VerifiedHTMLBox
            className="markdown-body"
            html={extractCommentBodyHTML(commentPreview)}
            sx={{
              p: 3,
              fontSize: 1,
            }}
          />
        </>
      )}
    </div>
  )
})

export function extractCommentAuthors(commentId: string, commentsData: Comments): Author[] {
  const uniqueAuthors = new Set<string>()
  const commentsArray = commentsData?.comments ?? []

  const extractedAuthors = commentsArray.reduce((authors, comment) => {
    // Skip the current comment (match by id)
    if (!comment || comment.id === commentId) return authors

    const login = comment.author?.login ?? ''
    const avatarUrl = comment.author?.avatarUrl ?? ''
    const id = comment.author?.id ?? ''
    const url = comment.author?.url ?? ''

    // Use login as the uniqueness key (empty string allowed)
    if (!uniqueAuthors.has(login)) {
      authors.push({
        avatarUrl,
        login,
        id,
        url,
      })
      uniqueAuthors.add(login)
    }

    return authors
  }, [] as Author[])

  return extractedAuthors
}

function extractCommentBodyHTML(commentPreview: PendingCommentPreviewType): SafeHTMLString {
  const commentsArray = commentPreview.commentsData?.comments ?? []
  const matchedComment = commentsArray.find(
    comment => comment && String(comment.databaseId) === commentPreview.commentId,
  )
  const html = matchedComment?.bodyHTML ?? ''

  return html as SafeHTMLString
}

try{ PendingCommentPreview.displayName ||= 'PendingCommentPreview' } catch {}